import { useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { useBreakpoint } from '@ocean/hooks'
import { Mode } from '@ocean/types/dist/appearance'
import { common, modals } from '@ocean/ui'
import { PermissionUtils, UserUtils } from 'utils'
import { useMobile } from 'hooks'
import { NotificationsNumbers } from 'core/environment/types'
import { useRedirectToRequirements } from 'core/staIntegration/hooks'
import { LegacyMenuItem, StaRedirectParams } from 'core/staIntegration/types'
import { PromoInfo } from 'components/common'
import { NavTree } from 'components/navigation'
import { AccountTypes } from 'modules/Auth/types'
import homeRoutes from 'modules/Home/routing/routes'
import profileRoutes from 'modules/Profile/routing/routes'
import searchRoutes from 'modules/Search/routing/routes'
import requirementsRoutes from 'modules/requirements/Requirements/routing/routes'
import trainingLibraryRoutes from 'modules/trainingLibrary/TrainingLibrary/routing/routes'
import AssessmentPlatformMenuItem from './AssessmentPlatformMenuItem'
import ProgramBuilderMenuItem from './ProgramBuilderMenuItem'
import {
  NavigationRoot,
  NavigationLink,
  NavigationIcon,
  NavigationContent,
  NavigationButton,
  PromoWrapper,
} from './SectionsNav.styled'

enum NavItemType {
  Link,
  Button,
}

type NavItem = {
  type: NavItemType
  icon: JSX.Element
  keyTitle: string
  dataTest: string
  to: string
  onClick?: Callback
  hidden?: boolean
}
const { Icon } = common
const { Popup } = modals

type SectionsNavProps = {
  legacyMenuItems?: LegacyMenuItem[]
  notificationsCounters?: NotificationsNumbers
  onRedirect?: (redirectParams: StaRedirectParams) => void
}

const SectionsNav: React.FC<SectionsNavProps> = props => {
  const { legacyMenuItems, notificationsCounters, onRedirect } = props
  const [isModalOpen, setModalVisibility] = useState(false)
  const location = useLocation()
  const isDesktopDevice = useBreakpoint('sm')
  const isMobileDevice = useMobile()
  const isDesktop = isDesktopDevice && !isMobileDevice
  const handleRedirectToRequirements = useRedirectToRequirements(isDesktop)
  const { t } = useTranslation()
  const { type } = UserUtils.getUserAccountInfo()
  const isSpecialUser = type === AccountTypes.SpecialUser

  const LINKS: NavItem[] = [
    {
      type: NavItemType.Link,
      to: homeRoutes.root,
      icon: <Icon icon='home' />,
      keyTitle: 'navigation.home',
      dataTest: 'Home',
    },
    {
      type: NavItemType.Button,
      to: requirementsRoutes.root,
      onClick: handleRedirectToRequirements,
      icon: <Icon icon='list' />,
      keyTitle: !isDesktop ? 'navigation.requirementsShort' : 'navigation.requirements',
      hidden: PermissionUtils.requirementsDenied(),
      dataTest: 'Requirements',
    },
    {
      type: NavItemType.Link,
      to: trainingLibraryRoutes.root,
      icon: <Icon icon='library' />,
      keyTitle: !isDesktop ? 'navigation.libraryShort' : 'navigation.library',
      hidden: PermissionUtils.libraryDenied(),
      dataTest: 'Library',
    },
    {
      type: NavItemType.Link,
      to: searchRoutes.root,
      icon: <Icon icon='search' />,
      keyTitle: 'navigation.search',
      hidden: PermissionUtils.searchDenied(),
      dataTest: 'Search',
    },
    {
      type: NavItemType.Button,
      to: profileRoutes.root,
      onClick: redirectToProfile,
      icon: <Icon icon='user' />,
      keyTitle: 'navigation.profile',
      dataTest: 'Profile',
    },
  ]

  const handleToggleModal = useCallback(() => {
    setModalVisibility(!isModalOpen)
  }, [isModalOpen])

  function redirectToProfile() {
    if (onRedirect) {
      onRedirect({ pluginName: 'Personnel', moduleName: null, command: 'PERSONALCARD' })
    }
  }

  function renderLegacyMenu() {
    if (!onRedirect) return null
    return (
      <>
        <NavTree items={legacyMenuItems} counters={notificationsCounters} onRedirect={onRedirect} />
        <ProgramBuilderMenuItem />
        <AssessmentPlatformMenuItem />
        <PromoWrapper>
          <PromoInfo />
        </PromoWrapper>
      </>
    )
  }

  function renderNavItemContent({ icon, keyTitle }: NavItem) {
    return (
      <>
        <NavigationIcon data-test='SidebarNavigationLinkIcon'>{icon}</NavigationIcon>
        <NavigationContent isDesktop={isDesktop} data-test='SidebarNavigationLinkTitle'>
          {t(keyTitle)}
        </NavigationContent>
      </>
    )
  }

  function renderMenu() {
    if (isSpecialUser) return null
    return (
      <>
        {LINKS.map(navItem => {
          const { type, to = '', onClick, hidden, dataTest } = navItem
          const isActive = to && location.pathname.includes(to)
          if (hidden) return null
          if (type === NavItemType.Button) {
            return (
              <NavigationButton
                key={dataTest}
                isDesktop={isDesktop}
                onClick={onClick}
                className={isActive ? 'active' : ''}
                data-test={`SidebarNavigationLink${dataTest}`}
              >
                {renderNavItemContent(navItem)}
              </NavigationButton>
            )
          }
          return (
            <NavigationLink
              key={dataTest}
              to={to}
              delay={1000}
              isDesktop={isDesktop}
              dataTest={`SidebarNavigationLink${dataTest}`}
            >
              {renderNavItemContent(navItem)}
            </NavigationLink>
          )
        })}
      </>
    )
  }

  return (
    <NavigationRoot isDesktop={isDesktop} data-test='SidebarNavigation'>
      {renderMenu()}
      {!isDesktop && legacyMenuItems && (
        <>
          <NavigationButton
            onClick={handleToggleModal}
            isDesktop={isDesktop}
            data-test='SidebarNavigationMoreButton'
          >
            <NavigationIcon data-test='SidebarNavigationLinkIcon'>
              <Icon icon='menu' />
            </NavigationIcon>
            <NavigationContent isDesktop={isDesktop} data-test='SidebarNavigationLinkTitle'>
              {t('navigation.more')}
            </NavigationContent>
          </NavigationButton>
          <Popup
            isOpen={isModalOpen}
            onClose={handleToggleModal}
            title={t('navigation.more')}
            mode={Mode.Dark}
          >
            {renderLegacyMenu()}
          </Popup>
        </>
      )}
    </NavigationRoot>
  )
}

export default memo(SectionsNav)
